/**
 * Tracking App Component
 *
 * Main application component that manages the tracking interface
 */

import type {
  TrackingConfig,
  TrackingFormState,
  TrackingRequest,
  TrackingRequestEvent,
  TrackingResponse,
  Subscription,
} from '@/types';
import { ApiService } from '@/services/ApiService';
import { EventEmitter } from '@/utils/EventEmitter';
import { TimezoneManager } from '@/utils/TimezoneManager';
import { ModalManager } from '@/utils/ModalManager';
import { TranslationHelper } from '@/utils/TranslationHelper';
import moment from 'moment';
import 'moment/dist/locale/fr';

import { 
  MapPin, 
  Home, 
  User, 
  Users, 
  Truck, 
  AlertCircle, 
  Building2, 
  Weight, 
  Calendar, 
  CalendarClock,
  CalendarCheck,
  Hash,
  FileText,
  createElement
} from 'lucide';

export class TrackingApp {
  private container: HTMLElement;
  private apiService: ApiService;
  private eventEmitter: EventEmitter;
  private config: TrackingConfig;
  private state: TrackingFormState;
  private subscriptions: Subscription[] = [];
  private timezoneManager: TimezoneManager;
  private modalManager: ModalManager;
  private standardTrackingStatuses: any = null;
  private standardTrackingStatusesLoading: Promise<void> | null = null;

  // DOM elements
  private searchForm: HTMLFormElement | null = null;
  private trackingInput: HTMLTextAreaElement | null = null;
  private postalCodeInput: HTMLInputElement | null = null;
  private searchButton: HTMLButtonElement | null = null;
  private resultsContainer: HTMLElement | null = null;
  private loadingContainer: HTMLElement | null = null;

  constructor(
    container: HTMLElement,
    apiService: ApiService,
    eventEmitter: EventEmitter,
    config: TrackingConfig
  ) {
    this.container = container;
    this.apiService = apiService;
    this.eventEmitter = eventEmitter;
    this.config = config;
    this.timezoneManager = new TimezoneManager(eventEmitter);
    this.modalManager = ModalManager.getInstance();

    this.state = {
      trackingNumbers: '',
      postalCode: '',
      isLoading: false,
      error: null,
      hasSearched: false,
    };
  }

  /**
   * Initialize the tracking app
   */
  public init(): void {
    try {
      this.findDomElements();
      this.bindEvents();
      this.setupEventListeners();
      
      
      this.handleInitialLoad();

      // Initialize managers after DOM is ready
      setTimeout(() => {
        this.timezoneManager.init();
        // Modal event delegation is handled globally in main.ts
        
        // Show container once everything is initialized
        this.container.style.display = '';
      }, 100);

      // Initialization completed successfully
    } catch (error) {
      console.error('[TrackingApp] Initialization failed:', error);
      this.showError('Failed to initialize tracking application');
    }
  }


  /**
   * Destroy the tracking app and clean up
   */
  public destroy(): void {
    // Unsubscribe from all events
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];

    // Remove event listeners
    if (this.searchForm) {
      this.searchForm.removeEventListener('submit', this.handleFormSubmit);
    }

    // Cleanup managers
    this.timezoneManager.destroy();
    // Component cleanup completed
  }

  /**
   * Find DOM elements in the container
   */
  private findDomElements(): void {
    this.searchForm = this.container.querySelector('[data-tracking-form]') as HTMLFormElement;
    this.trackingInput = this.container.querySelector(
      '[data-tracking-input]'
    ) as HTMLTextAreaElement;
    this.postalCodeInput = this.container.querySelector('[data-postal-input]') as HTMLInputElement;
    this.searchButton = this.container.querySelector('[data-search-button]') as HTMLButtonElement;
    this.resultsContainer = this.container.querySelector('.gta-results-container') as HTMLElement;
    this.loadingContainer = this.container.querySelector('.gta-loading-container') as HTMLElement;

    // If elements don't exist, create them (fallback for minimal HTML)
    if (!this.resultsContainer) {
      this.resultsContainer = document.createElement('div');
      this.resultsContainer.className = 'gta-results-container';
      this.container.appendChild(this.resultsContainer);
    }

    // Loading container should already exist in PHP template
    // If not found, log warning but don't create fallback to avoid duplicating PHP template logic
    if (!this.loadingContainer) {
      console.warn(
        '[TrackingApp] Loading container not found in template. Ensure tracking-container.php is being used.'
      );
    }
  }

  /**
   * Bind DOM event listeners
   */
  private bindEvents(): void {
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    if (this.trackingInput) {
      this.trackingInput.addEventListener('input', this.handleTrackingInputChange.bind(this));
    }

    if (this.postalCodeInput) {
      this.postalCodeInput.addEventListener('input', this.handlePostalCodeInputChange.bind(this));
    }

    // Email subscription form (keep this for form submission handling)
    document.addEventListener('submit', this.handleEmailFormSubmit.bind(this));

    // Modal event delegation is handled by ModalManager in init()

    // Postal code forms in individual shipment cards
    this.container.addEventListener('submit', this.handlePostalCodeFormSubmit.bind(this));

    // Address update modal interactions
    this.container.addEventListener('click', this.handleUpdateAddressClick.bind(this));
    this.container.addEventListener('submit', this.handleUpdateAddressSubmit.bind(this));
  }

  /**
   * Setup application event listeners
   */
  private setupEventListeners(): void {
    // Listen for external tracking requests
    const trackingRequestSub = this.eventEmitter.on(
      'trackingRequested',
      (data?: TrackingRequestEvent) => {
        if (data) {
        this.handleExternalTrackingRequest(data);
        } else {
          console.error('No data received for trackingRequested event');
        }
      });
    this.subscriptions.push(trackingRequestSub);
  }

  /**
   * Open email modal with tracking data using ModalManager
   * This method can be called directly when needed
   */
  public openEmailModal(trackingNumber: string, postalCode: string = ''): void {
    this.modalManager.openEmailModal(trackingNumber, postalCode || this.state.postalCode);
  }

  /**
   * Handle email subscription form submission
   */
  private handleEmailFormSubmit(event: Event): void {
    if ((event.target as HTMLElement).id === 'gta-email-subscribe-form') {
      this.handleEmailSubscription(event);
    }
  }

  /**
   * Open image modal with specified image URL using ModalManager
   * This method can be called directly when needed
   */
  public openImageModal(imageUrl: string, altText: string = 'Proof of Delivery Image'): void {
    this.modalManager.openImageModal(imageUrl, altText);
  }

  /**
   * Handle postal code form submission within shipment cards
   */
  private async handlePostalCodeFormSubmit(event: Event): Promise<void> {
    const form = event.target as HTMLFormElement;

    // Skip address update modal forms
    if (form?.dataset?.updateAddressForm === 'true') {
      return;
    }

    // Check if this is a postal code form (has postal_code input)
    const postalCodeInput = form.querySelector('input[name="gta_postal_code"]') as HTMLInputElement;
    const trackingNumberElement = form.closest('.gta-shipment-card');

    if (postalCodeInput && trackingNumberElement) {
      event.preventDefault();

      const trackingNumber = trackingNumberElement.getAttribute('data-tracking-number');
      const postalCode = postalCodeInput.value.trim();

      if (trackingNumber && postalCode) {
        await this.reSearchWithPostalCode(trackingNumber, postalCode);
      }
    }
  }

  /**
   * Re-search for a specific tracking number with postal code
   */
  private async reSearchWithPostalCode(trackingNumber: string, postalCode: string): Promise<void> {
    try {
      // Update state with postal code
      this.state.postalCode = postalCode;
      if (this.postalCodeInput) {
        this.postalCodeInput.value = postalCode;
      }

      // Trigger search for the specific tracking number
      const request: TrackingRequest = {
        trackingNumbers: [trackingNumber],
        postalCode,
      };

      this.setState({ isLoading: true, error: null });

      const response: TrackingResponse = await this.apiService.getTrackingData(request);

      if (!response.success) {
        this.showError(response.error || 'Failed to fetch tracking data');
        return;
      }

      // Process the response data and render shipment card
      // Since we're now getting direct data instead of HTML, we need to handle rendering differently
      this.processTrackingResults(response.data || []);

      // Emit success event
      this.eventEmitter.emit('trackingSuccess', {
        shipmentsCount: response.data?.length || 0,
        notFoundCount: response.data ? (1 - response.data.length) : 1,
        requestedNumbers: [trackingNumber],
      });
    } catch (error) {
      console.error('[TrackingApp] Postal code search failed:', error);
      this.showError('Failed to update tracking data with postal code');
    } finally {
      this.setState({ isLoading: false });
    }
  }


  /**
   * Handle email subscription form submission
   */
  private async handleEmailSubscription(event: Event): Promise<void> {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const feedback = document.getElementById('gta-subscribe-feedback') as HTMLElement;

    if (!feedback) {
      return;
    }

    try {
      feedback.classList.add('hidden');

      // Use WordPress admin-ajax.php for email subscription
      const result = await this.subscribeToEmailNotifications({
        trackingNumber: formData.get('tracking_number') as string,
        emailAddress: formData.get('email_address') as string,
      });

      // Show feedback
      feedback.classList.remove('hidden');
      if (result.success) {
        feedback.className =
          'text-sm p-3 rounded bg-green-50 text-green-700 border border-green-200';
        feedback.textContent = 'Subscription successful!';

        // Reset form
        form.reset();
      } else {
        feedback.className =
          'text-sm p-3 rounded bg-red-50 text-red-700 border border-red-200';
        feedback.textContent = result.error || 'Subscription failed. Please try again.';
      }
    } catch (error) {
      console.error('[TrackingApp] Email subscription failed:', error);
      feedback.classList.remove('hidden');
      feedback.className =
        'text-sm p-3 rounded bg-red-50 text-red-700 border border-red-200';
      feedback.textContent = 'An error occurred. Please try again.';
    }
  }

  /**
   * Subscribe to email notifications via API endpoint
   */
  private async subscribeToEmailNotifications(data: {
    trackingNumber: string;
    emailAddress: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      // Get current language from HTML lang attribute
      const currentLang = document.getElementsByTagName('html')[0].getAttribute('lang') || 'en';

      // Prepare JSON payload for API
      const payload = {
        TrackingNumber: data.trackingNumber,
        EmailAddress: data.emailAddress,
        Language: currentLang
      };

      // Build API endpoint
      const endpoint = this.apiService.buildEndpoint('subscribe');

      // Make request to API endpoint
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.result || result.result) {
        return {
          success: true,
        };
      } else {
        return {
          success: false,
          error: result.error || result.message || result.Message || 'Email subscription failed',
        };
      }
    } catch (error) {
      console.error('[TrackingApp] API email subscription failed:', error);
      return {
        success: false,
        error: 'An unexpected error occurred while subscribing',
      };
    }
  }

  /**
   * Handle initial load (URL parameters, etc.)
   */
  private handleInitialLoad(): void {
    // Get initial values from form or URL
    const urlParams = new URLSearchParams(window.location.search);
    const trackingParam = urlParams.get('tracking') || urlParams.get('trackingid');
    const postalParam = urlParams.get('postal');

    if (trackingParam) {
      this.state.trackingNumbers = trackingParam;
      if (this.trackingInput) {
        this.trackingInput.value = trackingParam;
      }
    }

    if (postalParam) {
      this.state.postalCode = postalParam;
      if (this.postalCodeInput) {
        this.postalCodeInput.value = postalParam;
      }
    }

    // Ensure search form remains visible when tracking parameters are present
    // The search form should always be visible - this ensures it stays that way
    const searchFormContainer = this.container.querySelector('.gta-search-form-container');
    if (searchFormContainer) {
      (searchFormContainer as HTMLElement).style.display = 'block';
    }

    // Auto-search if tracking numbers are provided
    if (trackingParam) {
      setTimeout(() => {
        this.performSearch();
      }, 100);
    }
  }

  /**
   * Handle form submission
   */
  private handleFormSubmit = (event: Event): void => {
    event.preventDefault();
    this.performSearch();
  };

  /**
   * Handle tracking input changes
   */
  private handleTrackingInputChange = (event: Event): void => {
    const target = event.target as HTMLTextAreaElement;
    this.state.trackingNumbers = target.value;
    
    // Clear postal code when tracking number changes (new search)
    this.state.postalCode = '';
    if (this.postalCodeInput) {
      this.postalCodeInput.value = '';
    }
    
    this.clearError();
  };

  /**
   * Handle postal code input changes
   */
  private handlePostalCodeInputChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    this.state.postalCode = target.value;
  };

  /**
   * Handle external tracking requests
   */
  private handleExternalTrackingRequest(data: TrackingRequestEvent): void {
    if (data.trackingNumbers && Array.isArray(data.trackingNumbers)) {
      this.state.trackingNumbers = data.trackingNumbers.join(', ');
      this.state.postalCode = data.postalCode || '';

      // Update form inputs
      if (this.trackingInput) {
        this.trackingInput.value = this.state.trackingNumbers;
      }
      if (this.postalCodeInput) {
        this.postalCodeInput.value = this.state.postalCode;
      }

      // Perform search
      this.performSearch();
    }
  }

  /**
   * Perform tracking search
   */
  private async performSearch(): Promise<void> {
    try {
      // Validate input
      const validation = this.validateInput();
      if (!validation.valid) {
        this.showError(validation.error || 'Invalid input');
        return;
      }

      // Prepare request
      const trackingNumbers = this.apiService.parseTrackingNumbers(this.state.trackingNumbers);
      const request: TrackingRequest = {
        trackingNumbers,
        postalCode: this.state.postalCode,
      };

      // Validate tracking numbers
      const numberValidation = this.apiService.validateTrackingNumbers(trackingNumbers);
      if (!numberValidation.valid) {
        this.showError(numberValidation.errors.join(', '));
        return;
      }

      // Update state and UI
      this.setState({ isLoading: true, error: null });
      this.showLoading();

      // Make API request
      const response: TrackingResponse = await this.apiService.getTrackingData(request);

      if (!response.success) {
        this.showError(response.error || 'Failed to fetch tracking data');
        return;
      }

      // Process the response data - now we get actual data instead of HTML
      await this.processTrackingResults(response.data || []);
      this.setState({ hasSearched: true });

      // Emit success event
      this.eventEmitter.emit('trackingSuccess', {
        shipmentsCount: response.data?.length || 0,
        notFoundCount: trackingNumbers.length - (response.data?.length || 0),
        requestedNumbers: trackingNumbers,
      });
    } catch (error) {
      console.error('[TrackingApp] Search failed:', error);
      this.showError('An unexpected error occurred while searching');

      // Emit error event
      this.eventEmitter.emit('trackingError', {
        error: this.apiService.getErrorMessage(error),
        trackingNumbers: this.apiService.parseTrackingNumbers(this.state.trackingNumbers),
      });
    } finally {
      this.setState({ isLoading: false });
      this.hideLoading();
    }
  }

  /**
   * Handle address update modal open/close via delegated click
   */
  private handleUpdateAddressClick(event: Event): void {
    const target = event.target as HTMLElement;
    const actionElement = target.closest('[data-action]') as HTMLElement | null;
    if (!actionElement) {
      return;
    }

    const action = actionElement.getAttribute('data-action');

    if (action === 'test-address-success') {
      event.preventDefault();
      const form = actionElement.closest('form[data-update-address-form="true"]') as HTMLFormElement | null;
      if (form) {
        const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
        const submitLabel = submitButton?.querySelector('[data-btn-label]') as HTMLElement | null;
        const submitSpinner = submitButton?.querySelector('[data-btn-spinner]') as HTMLElement | null;
        const feedback = form.querySelector('[data-feedback]') as HTMLElement | null;
        const modalId = form.getAttribute('data-modal-id') || '';
        this.handleAddressUpdateSuccess(form, feedback, submitButton, submitLabel, submitSpinner, modalId);
      }
      return;
    }

    if (action === 'open-update-address' || action === 'close-update-address') {
      event.preventDefault();
      const explicitTarget = actionElement.getAttribute('data-target');
      const modalId =
        (explicitTarget && explicitTarget.replace('#', '')) ||
        actionElement.closest('[data-update-address-modal]')?.id ||
        '';
      if (modalId) {
        this.toggleUpdateAddressModal(modalId, action === 'open-update-address');
      }
    }
  }

  /**
   * Toggle address update modal visibility
   */
  private toggleUpdateAddressModal(modalId: string, show: boolean): void {
    const modal = modalId ? document.getElementById(modalId) : null;

    if (show) {
      if (modal) {
        modal.classList.remove('hidden');
        modal.classList.add('flex');
      }
      // Prevent background scroll while modal open
      document.body.classList.add('overflow-hidden');
      return;
    }

    // Closing: always restore scroll even if modal is missing
    if (modal) {
      modal.classList.add('hidden');
      modal.classList.remove('flex');
    }

    setTimeout(() => {
      document.body.classList.remove('overflow-hidden') 
    }, 100);
  }

  /**
   * Handle address update form submission
   */
  private async handleUpdateAddressSubmit(event: Event): Promise<void> {
    const form = event.target as HTMLFormElement;
    if (!form || form.dataset.updateAddressForm !== 'true') {
      return;
    }

    event.preventDefault();

    const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;

    // Prevent multiple submissions - if button is already disabled, ignore this submission
    if (submitButton?.disabled) {
      return;
    }

    const submitLabel = submitButton?.querySelector('[data-btn-label]') as HTMLElement | null;
    const submitSpinner = submitButton?.querySelector('[data-btn-spinner]') as HTMLElement | null;
    const feedback = form.querySelector('[data-feedback]') as HTMLElement | null;
    const modalId = form.getAttribute('data-modal-id') || '';
    const trackingNumber = form.getAttribute('data-tracking-number') || '';

    const formData = new FormData(form);
    const payload = {
      TrackingNumber: trackingNumber,
      ContactName: (formData.get('contactName') as string || '').trim(),
      ContactPhone: (formData.get('contactPhone') as string || '').trim(),
      ContactEmail: (formData.get('contactEmail') as string || '').trim(),
      Address1: (formData.get('address1') as string || '').trim(),
      Address2: (formData.get('address2') as string || '').trim(),
      City: (formData.get('city') as string || '').trim(),
      State: (formData.get('state') as string || '').trim(),
      PostalCode: (formData.get('postalCode') as string || '').trim(),
      Country: 'Canada',
      Comment: (formData.get('comment') as string || '').trim()
    };

    const missingFields: string[] = [];
    if (!payload.ContactName) { missingFields.push('Contact Name'); }
    if (!payload.ContactPhone) { missingFields.push('Contact Phone'); }
    if (!payload.ContactEmail) { missingFields.push('Contact Email'); }
    if (!payload.Address1) { missingFields.push('Address Line 1'); }
    if (!payload.City) { missingFields.push('City'); }
    if (!payload.State) { missingFields.push('State / Province'); }
    if (!payload.PostalCode) { missingFields.push('Postal Code'); }
    if (!payload.TrackingNumber) { missingFields.push('Tracking Number'); }

    if (missingFields.length > 0) {
      this.showUpdateAddressFeedback(feedback, `Please fill: ${missingFields.join(', ')}`, 'error');
      return;
    }

    // Validate postal code (Canadian format: A1A 1A1 with optional space)
    const postalCodePattern = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    if (!postalCodePattern.test(payload.PostalCode)) {
      this.showUpdateAddressFeedback(
        feedback,
        TranslationHelper.t('address_update_postal_invalid', 'Invalid postal code. Please check and try again.'),
        'error'
      );
      return;
    }

    try {
      if (submitButton) {
        submitButton.disabled = true;
        if (submitLabel) {
          submitLabel.textContent = TranslationHelper.t('address_update_sending', 'Sending...');
        } else {
          submitButton.textContent = TranslationHelper.t('address_update_sending', 'Sending...');
        }
        submitSpinner?.classList.remove('hidden');
      }
      this.showUpdateAddressFeedback(feedback, '', 'clear');

      const endpoint = this.apiService.buildEndpoint('update-shipment-details');

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      let result: any = null;
      try {
        result = await response.json();
      } catch (e) {
        // ignore parse errors; handle by status
      }

      const success = response.ok && (result?.success !== false);
      if (!success) {
        const errorMsg = result?.error || result?.message || 'Failed to update address. Please try again.';
        this.showUpdateAddressFeedback(feedback, errorMsg, 'error');
        if (submitButton) {
          submitSpinner?.classList.add('hidden');
          submitButton.disabled = false;
          if (submitLabel) {
            submitLabel.textContent = TranslationHelper.t('address_update_submit', 'Update Address');
          } else {
            submitButton.textContent = TranslationHelper.t('address_update_submit', 'Update Address');
          }
        }
        return;
      }

      this.handleAddressUpdateSuccess(
        form,
        feedback,
        submitButton,
        submitLabel,
        submitSpinner,
        modalId
      );
    } catch (error) {
      console.error('[TrackingApp] Address update failed:', error);
      this.showUpdateAddressFeedback(
        feedback,
        TranslationHelper.t('address_update_error', 'An unexpected error occurred. Please try again.'),
        'error'
      );
      if (submitButton) {
        submitSpinner?.classList.add('hidden');
        submitButton.disabled = false;
        if (submitLabel) {
          submitLabel.textContent = TranslationHelper.t('address_update_submit', 'Update Address');
        } else {
          submitButton.textContent = TranslationHelper.t('address_update_submit', 'Update Address');
        }
      }
    } finally {
      // On success we leave the button disabled; on error it is re-enabled above
    }
  }

  /**
   * Show feedback inside the address update modal
   */
  private showUpdateAddressFeedback(
    feedbackEl: HTMLElement | null,
    message: string,
    type: 'success' | 'error' | 'clear'
  ): void {
    if (!feedbackEl) {
      return;
    }
    if (type === 'clear') {
      feedbackEl.className = 'hidden';
      feedbackEl.textContent = '';
      return;
    }

    feedbackEl.textContent = message;
    feedbackEl.className = `block text-sm rounded px-3 py-2 ${
      type === 'success'
        ? 'bg-green-50 text-green-700 border border-green-200'
        : 'bg-red-50 text-red-700 border border-red-200'
    }`;

    // Also surface as a toast so it is visible outside the form context
    if (type === 'success' || type === 'error') {
      this.showToast(message, type);
    }
  }

  /**
   * Lightweight toast helper (address update only)
   */
  private showToast(message: string, type: 'success' | 'error'): void {
    if (!message) {
      return;
    }
    const existing = document.querySelector('.gta-toast');
    if (existing) {
      existing.remove();
    }

    const toast = document.createElement('div');
    toast.className = `gta-toast fixed top-4 right-4 z-[10000] px-4 py-3 rounded shadow-lg text-sm font-medium ${
      type === 'success'
        ? 'bg-green-600 text-white'
        : 'bg-red-600 text-white'
    }`;
    toast.textContent = message;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('opacity-0', 'transition-opacity');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Handle address update success (shared by real submission and test button)
   */
  private handleAddressUpdateSuccess(
    form: HTMLFormElement,
    feedback: HTMLElement | null,
    submitButton: HTMLButtonElement | null,
    submitLabel: HTMLElement | null,
    submitSpinner: HTMLElement | null,
    modalId: string
  ): void {
    this.showUpdateAddressFeedback(
      feedback,
      TranslationHelper.t('address_update_success', 'Thank you! Your address was updated.'),
      'success'
    );

    const card = form.closest('.gta-shipment-card');
    const ctaWrapper = card?.querySelector('[data-address-update-cta]');
    ctaWrapper?.remove();

    submitSpinner?.classList.add('hidden');
    if (submitButton) {
      submitButton.disabled = true;
    }
    if (submitLabel) {
      submitLabel.textContent = TranslationHelper.t('address_update_success', 'Success');
    }

    // Close modal after showing success and restore scrolling
    setTimeout(() => {
      this.toggleUpdateAddressModal(modalId, false);
    }, 1200);
  }

  /**
   * Validate form input
   */
  private validateInput(): { valid: boolean; error?: string } {
    if (!this.state.trackingNumbers.trim()) {
      return {
        valid: false,
        error: 'Please enter at least one tracking number',
      };
    }

    return { valid: true };
  }

  /**
   * Update component state
   */
  private setState(newState: Partial<TrackingFormState>): void {
    this.state = { ...this.state, ...newState };
    this.updateUI();
  }

  /**
   * Update UI based on current state
   */
  private updateUI(): void {
    // Update button state using PHP template structure (gta-btn-text/gta-btn-loading spans)
    if (this.searchButton) {
      this.searchButton.disabled = this.state.isLoading;

      // Use existing PHP template button structure with CSS classes
      const btnText = this.searchButton.querySelector('.gta-btn-text');
      const btnLoading = this.searchButton.querySelector('.gta-btn-loading');

      if (btnText && btnLoading) {
        if (this.state.isLoading) {
          btnText.classList.add('hidden');
          btnLoading.classList.remove('hidden');
        } else {
          btnText.classList.remove('hidden');
          btnLoading.classList.add('hidden');
        }
      }
    }

    // Update form state
    if (this.searchForm) {
      this.searchForm.classList.toggle('gta-loading', this.state.isLoading);
    }
  }

  /**
   * Process tracking results from API data and render shipment cards
   */
  private async processTrackingResults(shipments: any[]): Promise<void> {
    if (!this.resultsContainer) {
      return;
    }

    if (!shipments || shipments.length === 0) {
      this.renderNoResults();
      return;
    }

    const foundCount = shipments.length;

    // Render shipment cards - handle async rendering
    const shipmentsHtmlPromises = shipments.map(shipment => this.renderShipmentCard(shipment, foundCount));
    const shipmentsHtmlArray = await Promise.all(shipmentsHtmlPromises);
    const shipmentsHtml = shipmentsHtmlArray.join('');
    
    this.resultsContainer.innerHTML = `
      <div class="gta-shipments-container">
        ${shipmentsHtml}
      </div>
    `;
    this.resultsContainer.style.display = 'block';

    // Refresh timezone conversions for newly displayed content
    setTimeout(() => {
      this.timezoneManager.refresh();
    }, 50);

    // Reinitialize modals for newly loaded content
    setTimeout(() => {
      this.modalManager.reinitializeModals();
    }, 60);

    // Reinitialize Swiper galleries for newly loaded content
    setTimeout(() => {
      const app = (
        window as typeof window & {
          GtaGsmTracking?: { getInstance: () => { reinitializeSwiper?: () => void } };
        }
      ).GtaGsmTracking?.getInstance();
      if (app && app.reinitializeSwiper) {
        app.reinitializeSwiper();
      }
    }, 100);
  }

  /**
   * Render a single shipment card
   */
  private async renderShipmentCard(shipment: any, foundCount: number = 1): Promise<string> {
    const trackingNumber = shipment.TrackingNumber || '';
    const cardId = `gta-shipment-${trackingNumber.replace(/[^a-zA-Z0-9]/g, '_')}`;
    
    // Get last event for timezone conversion
    const events = shipment.Events || [];
    const lastEvent = events.length > 0 ? events[events.length - 1] : null;
    
    // Process shipment status
    const status = await this.processShipmentStatus(shipment);
    
    // Use JSON color if available, otherwise fallback to colorType  
    const statusInfo = await this.getStandardStatusInfo(status.standardStatus);
    const actualStatusColor = this.getActualColorFromJson(statusInfo.color);
    const statusColorClass = actualStatusColor ? 
      `text-[${actualStatusColor}]` : 
      this.getStatusColorClass(status.statusLevel);
    
    // Determine if content should be expanded by default (when only one card)
    const isSingleCard = foundCount === 1;
    const contentHiddenClass = isSingleCard ? '' : 'hidden';
    const ariaExpanded = isSingleCard ? 'true' : 'false';
    const historyButtonText = isSingleCard 
      ? TranslationHelper.t('label_hide_tracking_history', 'Hide Tracking History')
      : TranslationHelper.t('label_show_tracking_history', 'Show Tracking History');
    // Details section should always start collapsed
    const detailsContentHiddenClass = 'hidden';
    const detailsAriaExpanded = 'false';
    const detailsButtonText = TranslationHelper.t('label_show_shipment_details', 'Show Shipment Details');

    return `
      <div id="${cardId}" 
        class="${status.cardCssClass || 'card'} gta-shipment-card animate-fade-in"
        data-tracking-number="${trackingNumber}">
        
        <!-- Card Header -->
        <div class="card-header flex justify-between items-start pb-4">
          <div class="flex flex-col gap-2">
            <p class="text-black text-xs! lg:text-sm! font-semibold mb-0!">
              ${TranslationHelper.t('label_last_updated', 'Last updated:')}
              <span class="time-convert text-primary-500" data-original="${lastEvent?.EventDateTime || ''}">
                ${status.lastUpdate || TranslationHelper.t('status_unknown', 'Unknown')}
              </span>
            </p>
            <h2 class="text-black text-xl! lg:text-3xl! font-medium mb-0!">
              ${TranslationHelper.t('label_status', 'Status:')}
              <span class="${statusColorClass}">
                ${status.stage || TranslationHelper.t('status_unknown', 'Unknown')}
              </span>
            </h2>
            <h3 class="text-sm! lg:text-base! font-normal! text-black mb-0!">
              ${TranslationHelper.t('label_tracking_number', 'Tracking number:')} ${trackingNumber}
            </h3>
          </div>

          ${shipment.AddressUpdated === false ? `
          <div class="w-full lg:w-auto lg:ms-6 mt-4 lg:mt-0">
            ${this.renderAddressUpdateCta(cardId, trackingNumber)}
          </div>
          ` : ''}

          ${TranslationHelper.getCurrentLang() !== 'fr_FR' ? `
          <div class="card-actions hidden lg:flex items-center">
            <button type="button" 
                class="gta-btn btn-email-modal whitespace-nowrap flex-shrink-0"
                data-modal-target="gta-email-modal" 
                data-modal-toggle="gta-email-modal"
                data-tracking="${trackingNumber}"
                title="${TranslationHelper.t('label_subscribe_email', 'Subscribe for Email Updates')}">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
                ${TranslationHelper.t('label_subscribe_email', 'Subscribe for Email Updates')}
            </button>
          </div>
          ` : ''}
        </div>

        ${status.estimatedDelivery && status.stage !== 'Delivered' ? `
          <!-- Estimated Delivery -->
          <div class="flex items-center gap-4 ps-4 p-2 bg-blue-50 w-full">
          <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M0.803356 0.969861C1.20956 0.563654 1.7605 0.335449 2.33496 0.335449H10.999C11.5735 0.335449 12.1244 0.563654 12.5306 0.969861C12.9368 1.37607 13.165 1.927 13.165 2.50146H18.5801C18.9903 2.50146 19.3653 2.73323 19.5487 3.10014L21.7148 7.43217C21.79 7.58255 21.8291 7.74837 21.8291 7.9165V12.2485C21.8291 12.8467 21.3442 13.3315 20.7461 13.3315H20.1662C20.1915 13.5084 20.2046 13.6892 20.2046 13.873C20.2046 15.9665 18.5075 17.6636 16.4141 17.6636C14.3206 17.6636 12.6235 15.9665 12.6235 13.873C12.6235 13.6231 12.6477 13.3787 12.6939 13.1422C12.5198 13.2617 12.3091 13.3315 12.082 13.3315H9.33613C9.36142 13.5084 9.37451 13.6892 9.37451 13.873C9.37451 15.9665 7.67743 17.6636 5.58398 17.6636C3.49053 17.6636 1.79346 15.9665 1.79346 13.873C1.79346 13.6892 1.80655 13.5084 1.83184 13.3315H1.25195C0.653824 13.3315 0.168945 12.8467 0.168945 12.2485V2.50146C0.168945 1.927 0.39715 1.37607 0.803356 0.969861ZM4.27571 12.9098C4.28157 12.9022 4.28733 12.8946 4.29299 12.8868C4.58986 12.4988 5.0577 12.2485 5.58398 12.2485C6.11027 12.2485 6.57811 12.4988 6.87498 12.8868C6.88064 12.8946 6.8864 12.9022 6.89226 12.9098C7.09102 13.1793 7.2085 13.5125 7.2085 13.873C7.2085 14.7702 6.48118 15.4976 5.58398 15.4976C4.68679 15.4976 3.95947 14.7702 3.95947 13.873C3.95947 13.5125 4.07695 13.1793 4.27571 12.9098ZM8.2368 11.1655C7.55313 10.4956 6.6168 10.0825 5.58398 10.0825C4.55117 10.0825 3.61483 10.4956 2.93117 11.1655H2.33496V2.50146H10.999V11.1655H8.2368ZM13.165 11.9195C13.8283 10.8187 15.0352 10.0825 16.4141 10.0825C17.4469 10.0825 18.3832 10.4956 19.0669 11.1655H19.6631V8.99951H15.3311C14.7329 8.99951 14.248 8.51463 14.248 7.9165C14.248 7.31838 14.7329 6.8335 15.3311 6.8335H18.9937L17.9107 4.66748H13.165V11.9195ZM17.7051 12.8868C17.4082 12.4988 16.9403 12.2485 16.4141 12.2485C15.5169 12.2485 14.7896 12.9759 14.7896 13.873C14.7896 14.7702 15.5169 15.4976 16.4141 15.4976C17.3113 15.4976 18.0386 14.7702 18.0386 13.873C18.0386 13.5125 17.9211 13.1793 17.7223 12.9098C17.7165 12.9022 17.7107 12.8946 17.7051 12.8868ZM6.66699 4.3065C7.26512 4.3065 7.75 4.79138 7.75 5.38951V6.38492L9.0573 7.6922C9.48024 8.11514 9.48025 8.80086 9.05731 9.2238C8.63437 9.64675 7.94865 9.64675 7.52571 9.22381L5.90119 7.59932C5.69809 7.39622 5.58398 7.12075 5.58398 6.83352V5.38951C5.58398 4.79138 6.06886 4.3065 6.66699 4.3065Z" fill="#008AD2"/>
          </svg>
          <div class="text-black text-2xl font-semibold">
            <span class="">${TranslationHelper.t('label_estimated_delivery', 'Estimated Delivery:')}</span>
            <span class=" text-primary-500">
              ${status.estimatedDelivery} ${TranslationHelper.t('label_before_9pm', '(Before 9PM)')}
            </span>
          </div>
        </div>
        ` : ''}
        
        
        <!-- Progress Bar -->
        <div class="progress-section py-6">
          ${this.renderProgressSteps(status.progress)}
            </div>

        ${TranslationHelper.getCurrentLang() !== 'fr_FR' ? `
        <div class="card-actions flex lg:hidden items-center justify-center my-5">
          <button type="button" 
            class="gta-btn btn-email-modal whitespace-nowrap flex-shrink-0"
            data-modal-target="gta-email-modal" 
            data-modal-toggle="gta-email-modal"
            data-tracking="${trackingNumber}"
            title="${TranslationHelper.t('label_subscribe_email', 'Subscribe for Email Updates')}">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            ${TranslationHelper.t('label_subscribe_email', 'Subscribe for Email Updates')}
          </button>
          </div>
        ` : ''}
        
        <!-- Toggle History Button -->
        <div class="flex justify-center mb-3">
          <button type="button" 
                class="text-primary-500 text-base! font-medium! flex items-center gap-2 p-2.5 rounded-lg"
                data-action="toggle-history"
                data-target="#${cardId}content-history"
                aria-expanded="${ariaExpanded}">
            ${historyButtonText}
            <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 8L10 13L15 8" stroke="#008AD2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Collapsed content - History -->
        <div id="${cardId}content-history" class="collapsed-content-history mt-6 ${contentHiddenClass}">
          <div class="flex flex-col lg:flex-row gap-10 pt-8 border-t-2 border-primary-200">
        <!-- Tracking History -->
            <div id="${cardId}-history" class="history-content lg:flex-1/2">
              <h4 class="text-black text-2xl! font-semibold leading-7 mb-5!">${TranslationHelper.t('label_tracking_history', 'Tracking History')}</h4>
              ${await this.renderTrackingHistory(shipment.Events || [])}
            </div>

            <!-- Proof of Delivery -->
            ${shipment.HasAttachments ? this.renderProofOfDelivery(shipment, cardId, status) : ''}
        </div>

          <!-- Shipment Details - Only show for local/qa environments -->
          ${this.shouldShowPackageDetails() ? `
            <div class="flex justify-center mt-10! mb-3">
              <button type="button" 
                    class="text-primary-500 text-base! font-medium! flex items-center gap-2 p-2.5 rounded-lg"
                    data-action="toggle-details"
                    data-target="#${cardId}content-details"
                    aria-expanded="${detailsAriaExpanded}">
                ${detailsButtonText}
                <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 8L10 13L15 8" stroke="#008AD2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            
            <!-- Collapsed content - Shipment Details -->
            <div id="${cardId}content-details" class="collapsed-content-details border-t-2 border-primary-200 ${detailsContentHiddenClass} mb-10!">
              <h4 class="text-black text-2xl! font-semibold leading-7 mt-6! mb-5!">${TranslationHelper.t('label_shipment_details', 'Package details')}</h4>
              ${this.renderShipmentDetails(shipment)}

              <div class="flex justify-end">
                <a href="mailto:customerservice@gtagsm.com" 
                class="gta-btn btn-secondary btn-sm no-underline! inline-flex!">
                  ${TranslationHelper.t('label_support', 'Support')} <svg xmlns="http://www.w3.org/2000/svg" class="ms-3" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-move-right-icon lucide-move-right"><path d="M18 8L22 12L18 16"/><path d="M2 12H22"/></svg>
                </a>
              </div>
            </div>
          ` : ''}
          </div>
        </div>
    `;
  }

  /**
   * Render address update CTA and modal for incomplete address status
   */
  private renderAddressUpdateCta(cardId: string, trackingNumber: string): string {
    const modalId = `${cardId}-update-address-modal`;
    return `
      <div class="flex flex-col gap-3" data-address-update-cta="true">
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
          ${TranslationHelper.t('address_update_needed', 'We need your help to complete the delivery address for this shipment.')}
        </div>
        <button
          type="button"
          class="gta-btn btn-primary w-full lg:w-auto text-base! py-3!"
          data-action="open-update-address"
          data-target="#${modalId}">
          ${TranslationHelper.t('address_update_button', 'Update Delivery Address')}
        </button>
        ${this.renderUpdateAddressModal(modalId, trackingNumber)}
      </div>
    `;
  }

  /**
   * Render update address modal markup
   */
  private renderUpdateAddressModal(modalId: string, trackingNumber: string): string {
    return `
      <div
        id="${modalId}"
        data-update-address-modal="true"
        class="gta-modal fixed inset-0 z-[9999] hidden items-center justify-center p-4 bg-black/50">
        <div class="bg-white w-full max-w-lg rounded-xl shadow-lg overflow-hidden max-h-[90vh] flex flex-col">
          <div class="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
            <h3 class="text-lg font-semibold text-black">
              ${TranslationHelper.t('address_update_modal_title', 'Update delivery address')}
            </h3>
            <button
              type="button"
              class="text-gray-500 hover:text-gray-700"
              aria-label="${TranslationHelper.t('label_close', 'Close')}"
              data-action="close-update-address"
              data-target="#${modalId}">
              &times;
            </button>
          </div>

          <form
            class="p-4 space-y-3 overflow-y-auto"
            data-update-address-form="true"
            data-tracking-number="${trackingNumber}"
            data-modal-id="${modalId}">
            
            <input type="hidden" name="trackingNumber" value="${trackingNumber}" />
            <input type="hidden" name="country" value="Canada" />

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  ${TranslationHelper.t('contact_name', 'Contact name')} <span class="text-red-500">*</span>
                </label>
                <input name="contactName" type="text" class="gta-input w-full" autocomplete="name" required />
              </div>
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  ${TranslationHelper.t('contact_phone', 'Phone')} <span class="text-red-500">*</span>
                </label>
                <input name="contactPhone" type="tel" class="gta-input w-full" autocomplete="tel" required />
              </div>
              <div class="form-group sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  ${TranslationHelper.t('contact_email', 'Email')} <span class="text-red-500">*</span>
                </label>
                <input name="contactEmail" type="email" class="gta-input w-full" autocomplete="email" required />
              </div>
            </div>

            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                ${TranslationHelper.t('address_line1', 'Address line 1')} <span class="text-red-500">*</span>
              </label>
              <input name="address1" type="text" class="gta-input w-full" required />
            </div>

            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                ${TranslationHelper.t('address_line2', 'Address line 2')}
              </label>
              <input name="address2" type="text" class="gta-input w-full" />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  ${TranslationHelper.t('city', 'City')} <span class="text-red-500">*</span>
                </label>
                <input name="city" type="text" class="gta-input w-full" required />
              </div>
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  ${TranslationHelper.t('state_province', 'State / Province')} <span class="text-red-500">*</span>
                </label>
                <input name="state" type="text" class="gta-input w-full" required />
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  ${TranslationHelper.t('postal_code', 'Postal code')} <span class="text-red-500">*</span>
                </label>
                <input name="postalCode" type="text" class="gta-input w-full" required />
              </div>
              <div class="form-group">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  ${TranslationHelper.t('country', 'Country')} <span class="text-red-500">*</span>
                </label>
                <input name="countryDisplay" type="text" class="gta-input w-full bg-gray-100" value="Canada" readonly />
              </div>
            </div>

            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                ${TranslationHelper.t('comment', 'Delivery Instructions (Buzzer Code)')}
              </label>
              <textarea name="comment" rows="3" class="gta-input w-full"></textarea>
            </div>

            <div data-feedback class="hidden" aria-live="polite"></div>

            <div class="flex flex-col sm:flex-row gap-3 sm:justify-end pt-2">
              <button
                type="submit"
                class="gta-btn btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
                data-submit-button="true">
                <span data-btn-label>${TranslationHelper.t('address_update_submit', 'Update Address')}</span>
                <span data-btn-spinner class="hidden w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  /**
   * Process shipment status information
   */
  private async processShipmentStatus(shipment: any): Promise<any> {
    const events = shipment.Events || [];
    const lastEvent = events.length > 0 ? events[events.length - 1] : null;
    const standardStatus = shipment.StandardStatus || (lastEvent?.StandardStatus);
    
    // Get status info from standard statuses with fallback
    const fallbackData = {
      status: shipment.Status,
      name: lastEvent?.Name,
      category: lastEvent?.Category
    };
    const statusInfo = await this.getStandardStatusInfo(standardStatus, fallbackData);
    
    return {
      statusLevel: statusInfo.colorType, // error, warning, info, success
      hasProblem: statusInfo.hasProblem,
      stage: statusInfo.name || lastEvent?.Stage || shipment.Status || 'Unknown',
      lastUpdate: lastEvent?.EventDateTime ? this.formatDate(lastEvent.EventDateTime) : '',
      estimatedDelivery: shipment.EstimatedDeliveryDate ? this.formatDate(shipment.EstimatedDeliveryDate) : '',
      cardCssClass: `card ${statusInfo.hasProblem ? 'card-warning' : 'card-success'}`,
      progress: this.buildProgressSteps(shipment, statusInfo),
      progressPercentage: this.calculateProgressPercentage(shipment, statusInfo),
      lastUpdateText: lastEvent?.description || statusInfo.description || '',
      lastLocation: lastEvent?.Location || '',
      standardStatus,
      statusDescription: statusInfo.description,
      statusAdditionalInfo: statusInfo.additionalInfo
    };
  }

  /**
   * Get standard status information from the JSON config
   */
  private async getStandardStatusInfo(standardStatusId: number, fallbackData?: any): Promise<any> {
    try {
      // If no standardStatusId provided, use fallback data
      if (!standardStatusId && fallbackData) {
        return {
          name: fallbackData.status || fallbackData.name || 'Unknown',
          description: fallbackData.name || fallbackData.description || 'Status information not available',
          additionalInfo: '',
          color: 'DEFAULT',
          colorType: 'info',
          hasProblem: false
        };
      }

      // Load the standard tracking statuses if not already loaded
      if (!this.standardTrackingStatuses) {
        // If not already loading, start loading
        if (!this.standardTrackingStatusesLoading) {
          this.standardTrackingStatusesLoading = this.loadStandardTrackingStatuses();
        }
        // Wait for loading to complete
        await this.standardTrackingStatusesLoading;
      }

      const status = this.standardTrackingStatuses?.statuses?.find((s: any) => s.id === standardStatusId);
      
      if (!status) {
        // Fallback to provided data if no standard status found
        if (fallbackData) {
          return {
            name: fallbackData.status || fallbackData.category || fallbackData.name || 'Unknown',
            description: fallbackData.name || fallbackData.description || 'Status information not available',
            additionalInfo: '',
            color: 'DEFAULT',
            colorType: 'info',
            hasProblem: false
          };
        }
        
        return { 
          name: 'Unknown', 
          description: 'Status information not available', 
          color: 'DEFAULT', 
          colorType: 'info', 
          hasProblem: false 
        };
      }

      // Map color to colorType for easier use
      const colorTypeMapping: Record<string, string> = {
        'RED': 'error',
        'YELLOW': 'warning', 
        'GREEN': 'success',
        'DEFAULT': 'info'
      };

      const colorType = colorTypeMapping[status.color] || 'info';
      const hasProblem = colorType === 'error' || colorType === 'warning';

      // Replace email placeholders in additional info
      let additionalInfo = status.additionalInfo || '';
      if (additionalInfo && this.standardTrackingStatuses?.config?.contactInfo?.customerServiceEmail) {
        additionalInfo = additionalInfo.replace(
          /\{\{customerServiceEmail\}\}/g,
          this.standardTrackingStatuses.config.contactInfo.customerServiceEmail
        );
      }

      return {
        name: status.name,
        description: status.description,
        additionalInfo,
        color: status.color,
        colorType,
        hasProblem
      };
    } catch (error) {
      console.error('[TrackingApp] Error getting standard status info:', error);
      
      // Fallback to provided data if error occurs
      if (fallbackData) {
        return {
          name: fallbackData.status || fallbackData.category || fallbackData.name || 'Unknown',
          description: fallbackData.name || fallbackData.description || 'Status information not available',
          additionalInfo: '',
          color: 'DEFAULT',
          colorType: 'info',
          hasProblem: false
        };
      }
      
      return { 
        name: 'Unknown', 
        description: 'Status information not available', 
        color: 'DEFAULT', 
        colorType: 'info', 
        hasProblem: false 
      };
    }
  }

  /**
   * Load standard tracking statuses from JSON file
   */
  private async loadStandardTrackingStatuses(): Promise<void> {
    try {
      const current_lang = document.getElementsByTagName('html')[0].getAttribute('lang');

      const pluginUrl = this.getPluginUrl();
      
      // Determine which status file to load based on language
      let statusFileName = 'standard-tracking-statuses.json';
      if (current_lang && current_lang.toLowerCase().startsWith('fr')) {
        statusFileName = 'standard-tracking-statuses-fr_FR.json';
      }
      
      const response = await fetch(`${pluginUrl}/src/assets/config/${statusFileName}`);
      
        if (!response.ok) {
        throw new Error(`Failed to load standard tracking statuses: ${response.status}`);
      }
      this.standardTrackingStatuses = await response.json();
    } catch (error) {
      console.error('[TrackingApp] Failed to load standard tracking statuses:', error);
      // Set a fallback minimal config
      this.standardTrackingStatuses = {
        config: {
          contactInfo: {
            customerServiceEmail: 'customerservice@gtagsm.com'
          }
        },
        statuses: []
      };
    } finally {
      // Reset the loading promise once done
      this.standardTrackingStatusesLoading = null;
    }
  }

  /**
   * Get status color class based on status level
   */
  private getStatusColorClass(statusLevel: string): string {
    const statusColors: Record<string, string> = {
      'error': 'text-red-500',
      'warning': 'text-yellow-600',
      'success': 'text-green-600',
      'info': 'text-primary-500'
    };
    return statusColors[statusLevel] || statusColors['info'];
  }

  /**
   * Get actual color value from JSON configuration
   */
  private getActualColorFromJson(colorKey: string): string | null {
    if (!this.standardTrackingStatuses?.config?.colors) {
      return null;
    }
    return this.standardTrackingStatuses.config.colors[colorKey] || null;
  }

  /**
   * Get status index based on last event Category (fallback when no StandardStatus)
   */
  private getStatusIndexFromEventCategory(events: any[]): number {
    if (!events || events.length === 0) {
      return 0; // Default to first step
    }

    // Get the last event's category
    const lastEvent = events[events.length - 1];
    const category = lastEvent?.Category;

    if (!category) {
      return 0;
    }

    const categoryIndex = this.getCategoryStatusIndex(category);

    return categoryIndex;
  }

  /**
   * Get status index from a single event Category
   */
  private getCategoryStatusIndex(category: string): number {
    // Map categories to progress indices
    // 0 = Label Created, 1 = Received by GTA GSM, 2 = In Transit, 3 = Out For Delivery, 4 = Delivered
    switch (category.toLowerCase()) {
      case 'cancelled':
        return 2;
      case 'delayed':
        return 2;
      case 'out for delivery':
        return 3;
      case 'generic':
        return 2;
      case 'delivered':
        return 4;
      case 'shipped':
        return 1;
      case 'created':
        return 0;
      case 'lost by carrier':
        return 2;
      case 'returned to sender':
        return 2;
      case 'returning to sender':
        return 2;
      case 'customs cleared':
        return 2;
      case 'ready for shipment':
        return 2;
      default:
        return 0; // Default to first step
    }
  }

  /**
   * Get icon key from event Category (fallback when no StandardStatus)
   */
  private getIconKeyFromEventCategory(category: string): string {
    const stepKeys = ['labelCreated', 'receivedByGta', 'inTransit', 'outForDelivery', 'delivered'];
    const statusIndex = this.getCategoryStatusIndex(category);
    return stepKeys[statusIndex] || 'inTransit';
  }

  /**
   * Build progress steps based on shipment status
   */
  private buildProgressSteps(shipment: any, statusInfo: any): Record<string, any> {
    const steps = ['labelCreated', 'receivedByGta', 'inTransit', 'outForDelivery', 'delivered'];
    const progress: Record<string, any> = {};
    
    // Map shipment status/stage to progress steps
    const statusToStepMapping: Record<string, string> = {
      // Standard Status names (from JSON config)
      'Tendered': 'labelCreated',
      'Package Received': 'receivedByGta', 
      'In Transit': 'inTransit',
      'Out for Delivery': 'outForDelivery',
      'Delivered': 'delivered',
      
      // Legacy status/stage names (fallback)
      'Label Created': 'labelCreated',
      'Received by GTA GSM': 'receivedByGta',
      'Ready for Delivery': 'outForDelivery',
      'Next for Delivery': 'outForDelivery'
    };
    
    // Initialize all steps as not completed
    steps.forEach(step => {
      progress[step] = { isCompleted: false, hasError: false, cssClass: 'progress-bar-gray' };
    });
    
    // Determine current step based on shipment's current status
    const currentStatus = statusInfo.stage || shipment.Status || '';
    const currentStepKey = statusToStepMapping[currentStatus];
    
    let currentStepIndex = -1;
    if (currentStepKey) {
      currentStepIndex = steps.indexOf(currentStepKey);
    }
    
    // If no direct mapping found, try to infer from StandardStatus info
    if (currentStepIndex === -1 && statusInfo.name) {
      const inferredStepKey = statusToStepMapping[statusInfo.name];
      if (inferredStepKey) {
        currentStepIndex = steps.indexOf(inferredStepKey);
      }
    }
    
    // If still no mapping found, use event Category fallback
    if (currentStepIndex === -1) {
      const events = shipment.Events || [];
      currentStepIndex = this.getStatusIndexFromEventCategory(events);
    }
    
    // Mark all steps up to the current step as completed
    if (currentStepIndex >= 0) {
      for (let i = 0; i <= currentStepIndex; i++) {
        const step = steps[i];
        
        // All previous steps are always green/success
        if (i < currentStepIndex) {
          progress[step] = {
            isCompleted: true,
            hasError: false,
            cssClass: 'progress-bar-success',
            isLastCompleted: false
          };
        } else {
          // Current step - use status color or green if successful
          const hasError = statusInfo.hasProblem && (statusInfo.colorType === 'error' || statusInfo.colorType === 'warning');
          progress[step] = {
            isCompleted: true,
            hasError,
            cssClass: hasError ? 'progress-bar-error' : 'progress-bar-success',
            isLastCompleted: true
          };
        }
      }
    }
    
    return progress;
  }

  /**
   * Calculate progress percentage
   */
  private calculateProgressPercentage(shipment: any, statusInfo: any): number {
    const totalSteps = 5; // labelCreated, receivedByGta, inTransit, outForDelivery, delivered
    const progress = this.buildProgressSteps(shipment, statusInfo);
    const completed = Object.values(progress).filter((step: any) => step.isCompleted).length;
    return Math.round((completed / totalSteps) * 100);
  }

  /**
   * Format date for display
   */
  private formatDate(dateString: string): string {
    if (!dateString) {
      return '';
    }
    try {
      // Get current language for locale
      const currentLang = TranslationHelper.getCurrentLang();
      const momentLocale = currentLang === 'fr_FR' ? 'fr' : 'en';
      moment.locale(momentLocale);
      
      // Format: "Thu. Nov 14, 2024" or "Jeu. Nov 14, 2024"
      const format = momentLocale === 'fr' ? 'ddd MMM DD, YYYY' : 'ddd. MMM DD, YYYY';
      return moment(dateString).format(format);
    } catch {
      return dateString;
    }
  }

  /**
   * Render progress steps
   */
  private renderProgressSteps(progress: Record<string, any>): string {
    const steps = [
      { key: 'labelCreated', label: TranslationHelper.t('progress_label_created', 'Label Created'), icon: 'labelCreated' },
      { key: 'receivedByGta', label: TranslationHelper.t('progress_received', 'Received by GTA GSM'), icon: 'receivedByGta' },
      { key: 'inTransit', label: TranslationHelper.t('progress_in_transit', 'In Transit'), icon: 'inTransit' },
      { key: 'outForDelivery', label: TranslationHelper.t('progress_out_for_delivery', 'Out For Delivery'), icon: 'outForDelivery' },
      { key: 'delivered', label: TranslationHelper.t('status_delivered', 'Delivered'), icon: 'delivered' }
    ];

    const stepIcons = this.getStepIcons();

    const stepsHtml = steps.map(step => {
      const stepData = progress[step.key] || { isCompleted: false, hasError: false, isLastCompleted: false };
      const isCompleted = stepData.isCompleted;
      const hasError = stepData.hasError;
      const isLastCompleted = stepData.isLastCompleted;
      
      const stepClasses = ['status-step'];
      let iconState = 'pending';
      let labelState = 'pending';
      
      if (hasError) {
        stepClasses.push('error');
        iconState = 'error';
        labelState = 'error';
      } else if (isCompleted) {
        stepClasses.push('completed');
        iconState = 'completed';
        labelState = 'completed';
        
        if (isLastCompleted) {
          stepClasses.push('last-completed');
          iconState += ' last-completed';
        }
      }
      
      return `
        <li class="${stepClasses.join(' ')}">
          <div class="step-inner">
            <div class="step-icon ${iconState}" data-step="${step.key}">
              ${stepIcons[step.icon] || stepIcons['inTransit']}
            </div>
            <div class="step-label text-xs mt-2 text-center ${labelState}">
              ${step.label}
            </div>
          </div>
        </li>
      `;
    }).join('');

    return `
      <ol class="status-steps">
        ${stepsHtml}
      </ol>
    `;
  }

  /**
   * Get step icons SVG
   */
  private getStepIcons(): Record<string, string> {
    return {
      labelCreated: `<svg width="29" height="28" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M14.8587 5.83333C14.8587 5.83333 14.8588 5.83333 14.8587 5.83333V5.83333ZM14.869 5.83334L21.5443 5.84148C21.545 5.84148 21.5458 5.84149 21.5466 5.8415C21.8449 5.84393 22.1301 5.96427 22.34 6.17631C22.5504 6.38889 22.6679 6.67628 22.6667 6.9754L22.6667 13.3939C22.6564 13.4036 22.6463 13.4134 22.6364 13.4234L13.9774 22.1534L6.33471 14.4433L14.869 5.83334ZM14.8646 3.50001C14.8643 3.5 14.865 3.50001 14.8646 3.50001L21.5585 3.50818C22.4756 3.51376 23.3531 3.88299 23.9983 4.5348C24.6429 5.18605 25.0031 6.06626 25 6.98255C25 6.98335 25 6.98414 25 6.98493L23.8334 6.98017H25V6.98255V13.3967C25 13.7158 24.9344 14.0317 24.8072 14.3243C24.6835 14.6089 24.5042 14.8658 24.2798 15.0799L24.293 15.0666L23.4647 14.245L24.2605 15.0981C24.267 15.0921 24.2734 15.086 24.2798 15.0799L15.6151 23.8157L14.786 22.9951L15.6142 23.8166C15.4003 24.0324 15.1458 24.2039 14.8654 24.321C14.5846 24.4383 14.2833 24.4988 13.979 24.499C13.6747 24.4992 13.3734 24.4392 13.0924 24.3223C12.8114 24.2054 12.5564 24.034 12.342 23.818L4.67404 16.0824L5.50152 15.26L4.67295 16.0813C4.24066 15.6458 3.99805 15.057 3.99805 14.4433C3.99805 13.8294 4.24085 13.2404 4.67347 12.8048L13.2279 4.17448C13.443 3.95992 13.6982 3.7899 13.9791 3.67416C14.2597 3.55854 14.5612 3.49936 14.8646 3.50001ZM17.5135 9.8523C17.5135 9.20796 18.0359 8.68563 18.6802 8.68563H18.6919C19.3362 8.68563 19.8585 9.20796 19.8585 9.8523C19.8585 10.4966 19.3362 11.019 18.6919 11.019H18.6802C18.0359 11.019 17.5135 10.4966 17.5135 9.8523Z" fill="currentColor"/>
        </svg>`,
      receivedByGta: `<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.25 10.6172H17.468C16.95 10.6172 16.518 11.0132 16.472 11.5282C16.383 12.5232 16.226 13.3352 16.113 13.8342C15.348 14.0032 13.873 14.2722 12.251 14.2722C10.629 14.2722 9.153 14.0022 8.387 13.8332C8.273 13.3362 8.116 12.5262 8.028 11.5282C7.982 11.0122 7.55 10.6162 7.032 10.6162H2.25C1.698 10.6162 1.25 11.0642 1.25 11.6162C1.25 16.7912 2.396 20.5982 2.444 20.7582C2.544 21.0832 2.802 21.3362 3.13 21.4282C3.302 21.4772 7.402 22.6162 12.251 22.6162C17.1 22.6162 21.199 21.4772 21.371 21.4282C21.7 21.3352 21.959 21.0812 22.058 20.7542C22.107 20.5932 23.251 16.7472 23.251 11.6152C23.251 11.0632 22.803 10.6152 22.251 10.6152L22.25 10.6172ZM20.286 19.6412C19.041 19.9392 15.819 20.6172 12.25 20.6172C8.681 20.6172 5.454 19.9392 4.212 19.6412C3.939 18.5632 3.365 15.9412 3.265 12.6172H6.137C6.325 14.0132 6.596 14.9112 6.611 14.9562C6.71 15.2812 6.968 15.5342 7.296 15.6262C7.389 15.6522 9.62 16.2722 12.25 16.2722C14.88 16.2722 17.11 15.6522 17.204 15.6262C17.533 15.5332 17.792 15.2792 17.891 14.9532C17.905 14.9082 18.174 14.0062 18.362 12.6172H21.235C21.133 15.9102 20.559 18.5542 20.286 19.6412Z" fill="currentColor"/>
        <path d="M11.8996 11.3548C12.1186 11.4458 12.4296 11.4318 12.5996 11.3548C12.6896 11.3138 14.8906 10.4698 16.4966 7.36281C16.7506 6.87181 16.5586 6.26881 16.0676 6.01481C15.5766 5.76081 14.9736 5.95281 14.7196 6.44381C14.2406 7.36981 13.7146 8.03181 13.2486 8.49681V2.38281C13.2486 1.83081 12.8006 1.38281 12.2486 1.38281C11.6966 1.38281 11.2486 1.83081 11.2486 2.38281V8.49581C10.7816 8.03081 10.2566 7.36881 9.77763 6.44381C9.52363 5.95381 8.92063 5.75881 8.42963 6.01481C7.93963 6.26781 7.74663 6.87181 8.00063 7.36281C9.60763 10.4698 11.8066 11.3168 11.8976 11.3548H11.8996Z" fill="currentColor"/>
        </svg>`,
      inTransit: `<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.87988 17.9985L1.77246 15.2363L2.93716 13.7454H8.0828M9.55358 11.7925L1.77246 4.05519L3.68257 2.56438L14.8655 4.7391M12.2544 17.9173V23.063L10.7636 24.2277L8.0014 21.1202M21.2602 11.1346L23.4349 22.3176L21.9441 24.2277L14.2068 16.4466M18.4937 4.80064H19.1037C20.2615 4.80064 21.2001 5.73925 21.2001 6.8971V7.50627M8.90106 2.56436L8.05176 3.41366M12.6278 2.937L11.4836 4.0812M23.4359 17.0999L22.5866 17.9492M23.0627 13.3729L21.9185 14.5171M24.2275 4.56774V7.50627C24.2275 8.38413 23.8151 9.21102 23.1139 9.73914L6.4411 22.2958C6.10194 22.5519 5.68856 22.6905 5.26359 22.6905C4.1874 22.6905 3.30955 21.8186 3.30955 20.7364C3.30955 20.319 3.44358 19.904 3.70419 19.5589L16.2609 2.8861C16.789 2.1849 17.6159 1.77246 18.4937 1.77246H21.4323C22.976 1.77246 24.2275 3.02395 24.2275 4.56774Z" stroke="currentColor" stroke-width="2.3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`,
      outForDelivery: `<svg width="27" height="18" viewBox="0 0 27 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M15.4866 0.452148C16.0533 0.452168 16.5965 0.677486 16.9973 1.07825C17.398 1.47902 17.6234 2.02218 17.6234 2.58895H22.9667C23.3712 2.58909 23.741 2.8176 23.922 3.17927L26.0588 7.45377C26.1329 7.60203 26.1723 7.76564 26.1724 7.9314V12.2059C26.1724 12.796 25.6936 13.2747 25.1035 13.2747H24.5311C24.556 13.4492 24.5695 13.6274 24.5695 13.8087C24.5695 15.8742 22.8945 17.5483 20.829 17.5483C18.7636 17.5482 17.0894 15.8741 17.0894 13.8087C17.0894 13.5621 17.1136 13.3211 17.1592 13.0878C16.9875 13.2056 16.7793 13.2747 16.5554 13.2747H13.8462C13.8711 13.4491 13.8837 13.6274 13.8837 13.8087C13.8837 15.8741 12.2095 17.5481 10.1441 17.5483C8.07862 17.5483 6.40361 15.8742 6.40361 13.8087C6.40361 13.6274 6.41713 13.4492 6.44207 13.2747H5.86963C5.27949 13.2747 4.80078 12.796 4.80078 12.2059V8.33747H6.93848V11.137H7.52612C8.20065 10.4761 9.12513 10.0691 10.1441 10.0691C11.1629 10.0692 12.0868 10.4762 12.7612 11.137H15.4866V2.58895H6.93848H4.80123C4.80125 2.02229 5.02626 1.479 5.42688 1.07825C5.82767 0.677468 6.37168 0.452148 6.93848 0.452148H15.4866ZM10.1441 12.2059C9.62486 12.2059 9.16246 12.4527 8.86956 12.8356C8.864 12.8432 8.85832 12.8514 8.85256 12.8588C8.65666 13.1246 8.54132 13.4532 8.5413 13.8087C8.5413 14.6939 9.25891 15.4115 10.1441 15.4115C11.0292 15.4113 11.7469 14.6938 11.7469 13.8087C11.7469 13.4531 11.6307 13.1247 11.4348 12.8588C11.429 12.8514 11.4234 12.8432 11.4178 12.8356C11.125 12.4528 10.6632 12.206 10.1441 12.2059ZM20.829 12.2059C19.9439 12.206 19.2262 12.9236 19.2262 13.8087C19.2262 14.6939 19.9439 15.4114 20.829 15.4115C21.7142 15.4115 22.4318 14.6939 22.4318 13.8087C22.4318 13.453 22.3157 13.1247 22.1197 12.8588C22.1139 12.8513 22.1083 12.8432 22.1027 12.8356C21.8098 12.4529 21.3482 12.2059 20.829 12.2059ZM17.6234 11.8812C18.2778 10.7952 19.4687 10.0692 20.829 10.0691C21.848 10.0691 22.7725 10.476 23.447 11.137H24.0347V9.00024H19.7611C19.1709 9.00024 18.6922 8.52154 18.6922 7.9314C18.6924 7.34142 19.171 6.86344 19.7611 6.86344H23.3746L22.3057 4.72575H17.6234V11.8812Z" fill="currentColor"/>
        <path d="M1.54893 3.6001H11.7455M6.47458 5.49601L11.7455 5.49601M1.32807 7.34249L11.8249 7.34249" stroke="currentColor" stroke-width="0.915899" stroke-linecap="round"/>
        </svg>`,
      delivered: `<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.58527 19.378C7.36527 19.186 5.90527 18.806 5.22627 18.594C4.98927 17.637 4.50027 15.27 4.50027 12C4.50027 11.081 4.54927 10.176 4.62527 9.28695C6.21727 8.92395 8.84927 8.50095 12.5003 8.50095C18.5283 8.50095 21.7973 9.65495 22.1493 9.78695C22.7923 10.049 23.5523 9.49495 23.4983 8.80095C23.3413 5.57895 22.8803 3.47495 22.8603 3.38695C22.7833 3.04095 22.5293 2.76195 22.1933 2.65295C22.0483 2.60595 18.5883 1.50195 12.5003 1.50195C6.41227 1.50195 2.95027 2.60495 2.80627 2.65195C2.47027 2.76095 2.21627 3.04095 2.13927 3.38595C2.11927 3.47395 1.65927 5.57695 1.50127 8.79995C1.48427 9.13595 1.63927 9.45695 1.91027 9.65595C2.10427 9.79795 2.34127 9.85895 2.57427 9.84195C2.52927 10.554 2.49927 11.273 2.49927 12C2.49927 16.634 3.40727 19.547 3.44627 19.668C3.53527 19.947 3.74227 20.174 4.01127 20.287C4.08627 20.318 5.89627 21.065 9.41227 21.37C9.95427 21.42 10.4493 21.012 10.4943 20.46C10.5423 19.91 10.1353 19.426 9.58427 19.378H9.58527ZM3.98027 4.40195C5.14827 4.10995 8.11227 3.50095 12.4993 3.50095C16.8863 3.50095 19.8503 4.10995 21.0183 4.40195C21.1253 5.01695 21.2893 6.08895 21.4063 7.48095C19.7573 7.06295 16.7483 6.50095 12.5003 6.50095C8.25227 6.50095 5.24327 7.06295 3.59427 7.48095C3.71027 6.08995 3.87427 5.01695 3.98227 4.40195H3.98027ZM17.4993 10.5C13.3503 10.5 11.5003 12.35 11.5003 16.499C11.5003 20.648 13.3503 22.498 17.4993 22.498C21.6483 22.498 23.4983 20.648 23.4983 16.499C23.4983 12.35 21.6483 10.5 17.4993 10.5ZM17.4993 20.499C14.4713 20.499 13.4993 19.527 13.4993 16.499C13.4993 13.471 14.4713 12.499 17.4993 12.499C20.5273 12.499 21.4993 13.471 21.4993 16.499C21.4993 19.527 20.5273 20.499 17.4993 20.499ZM20.1413 14.864C20.5643 15.219 20.6193 15.849 20.2653 16.273C19.4753 17.214 18.5793 18.065 17.5983 18.799C17.4203 18.933 17.2093 18.999 16.9983 18.999C16.7873 18.999 16.5753 18.933 16.3973 18.799C15.8383 18.379 15.3003 17.917 14.7993 17.426C14.4043 17.039 14.3983 16.407 14.7833 16.012C15.1713 15.618 15.8033 15.612 16.1973 15.996C16.4553 16.248 16.7223 16.491 16.9983 16.722C17.6243 16.197 18.2053 15.615 18.7313 14.987C19.0873 14.566 19.7163 14.508 20.1403 14.863L20.1413 14.864Z" fill="currentColor"/>
        </svg>`
    };
  }

  /**
   * Render tracking history
   */
  private async renderTrackingHistory(events: any[]): Promise<string> {
    if (!events || events.length === 0) {
      return '<p class="text-gray-500">No tracking history available.</p>';
    }

    // Map event stage/status to icon keys (same as progress bar)
    const stageToIcon: Record<string, string> = {
      // Standard status names
      'Tendered': 'labelCreated',
      'Package Received': 'receivedByGta',
      'In Transit': 'inTransit',
      'Out for Delivery': 'outForDelivery',
      'Delivered': 'delivered',
      
      // Legacy stage names
      'Label Created': 'labelCreated',
      'Received by GTA GSM': 'receivedByGta',
      'Ready for Delivery': 'outForDelivery',
      'Next for Delivery': 'outForDelivery'
    };

    const stepIcons = this.getStepIcons();

    // Process events with async status info
    const eventsHtmlArray = await Promise.all(events.reverse().map(async (event, index) => {
      const eventStage = event.Stage || '';
      const eventStandardStatus = event.StandardStatus;
      
      // Get standard status info for this event with fallback
      const eventFallbackData = {
        status: event.Stage,
        name: event.Name,
        category: event.Category
      };
      const eventStatusInfo = await this.getStandardStatusInfo(eventStandardStatus, eventFallbackData);
      
      // Determine event color using actual JSON colors
      let eventColorClass = 'text-black';
      const eventActualColor = this.getActualColorFromJson(eventStatusInfo.color);
      if (eventActualColor) {
        eventColorClass = `text-[${eventActualColor}]`;
      } else {
        // Fallback to colorType classes
        if (eventStatusInfo.colorType === 'error') {
          eventColorClass = 'text-red-500';
        } else if (eventStatusInfo.colorType === 'warning') {
          eventColorClass = 'text-yellow-600';
        } else if (eventStatusInfo.colorType === 'success') {
          eventColorClass = 'text-green-600';
        }
      }
      
      // Determine icon based on status info, then fallback to stage, then to Category
      let iconKey = 'inTransit'; // default
      if (eventStatusInfo.name && stageToIcon[eventStatusInfo.name]) {
        iconKey = stageToIcon[eventStatusInfo.name];
      } else if (eventStage && stageToIcon[eventStage]) {
        iconKey = stageToIcon[eventStage];
      } else if (event.Name && stageToIcon[event.Name]) {
        iconKey = stageToIcon[event.Name];
      } else if (event.Category) {
        // Fallback to Category-based icon selection
        iconKey = this.getIconKeyFromEventCategory(event.Category);
      }
      
      const stageIcon = stepIcons[iconKey] || stepIcons['inTransit'];
      
      // Determine icon colors based on standard status with JSON colors
      let iconBgClass = 'bg-blue-100';
      const iconRingClass = 'ring-white';
      let iconTextClass = 'text-blue-800';
      
      const iconActualColor = this.getActualColorFromJson(eventStatusInfo.color);
      if (iconActualColor) {
        // Use actual JSON colors
        iconTextClass = `text-[${iconActualColor}]`;
        // Create a lighter background version
        iconBgClass = eventStatusInfo.colorType === 'success' ? 'bg-green-100' : 
                     eventStatusInfo.colorType === 'error' ? 'bg-red-100' :
                     eventStatusInfo.colorType === 'warning' ? 'bg-yellow-100' : 'bg-gray-100';
      } else {
        // Fallback to original logic
        if (eventStatusInfo.colorType === 'error') {
          iconBgClass = 'bg-red-100';
          iconTextClass = 'text-red-500';
        } else if (eventStatusInfo.colorType === 'warning') {
          iconBgClass = 'bg-yellow-100';
          iconTextClass = 'text-yellow-800';
        } else if (eventStage === 'Delivered') {
          iconBgClass = 'bg-green-100';
          iconTextClass = 'text-green-800';
        } else if (eventStage === 'Label Created') {
          iconBgClass = 'bg-gray-100';
          iconTextClass = 'text-gray-800';
        } else if (eventStatusInfo.colorType === 'success') {
          iconBgClass = 'bg-green-100';
          iconTextClass = 'text-green-800';
        }
      }
      
      const isLast = index === events.length - 1;

    return `
        <li class="static! flex flex-col gap-1 ms-8 lg:ms-11! ${!isLast ? 'mb-10' : ''}">
          <!-- Icon -->
          <span class="absolute flex items-center justify-center w-8 h-8 lg:w-11 lg:h-11 ${iconBgClass} rounded-full -start-4 lg:-start-5.5 ring-8 ${iconRingClass}">
            <div class="flex items-center justify-center w-5 h-5 lg:w-7 lg:h-7 ${iconTextClass}">
              ${stageIcon}
          </div>
          </span>
          <!-- Event Name -->
          <h3 class="text-base! lg:text-lg! capitalize font-semibold leading-7 mb-0! lg:mt-2! lg:mb-1! ${eventColorClass}">
            ${eventStatusInfo.name || event.Name || eventStage}
          </h3>
          <!-- Time -->
          <div class="flex items-center gap-2">
            <img src="${this.getPluginUrl()}/src/assets/images/calendar-icon.svg" alt="Clock" class="w-4 h-4">
            <time class="time-convert block text-gray-400 text-xs! font-medium leading-none" data-original="${event.EventDateTime || ''}">
              ${this.formatDate(event.EventDateTime || '')}
            </time>
          </div>
          <div class="event-name text-base">
            ${eventStatusInfo.description || event.Name || ''}
        </div>
          ${eventStatusInfo.additionalInfo ? `
            <div class="additional-info text-sm text-gray-600 italic">
              ${eventStatusInfo.additionalInfo}
      </div>
          ` : ''}
          <!-- Location -->
          ${event.Location ? `
            <span class="text-gray-600 text-base! font-normal leading-normal">${TranslationHelper.t('label_location', 'Location:')} ${event.Location}</span>
          ` : ''}
        </li>
      `;
    }));

    const eventsHtml = eventsHtmlArray.join('');

    return `
      <ol class="relative border-s border-gray-200 ms-5 lg:ms-0">
        ${eventsHtml}
      </ol>
    `;
  }

  /**
   * Get plugin URL
   */
  private getPluginUrl(): string {
    // This should match the PHP GTA_GSM_TRACK_PLUGIN_URL constant
    return '/wp-content/plugins/dwd-trackpackage-devdino';
  }

  /**
   * Render proof of delivery section
   */
  private renderProofOfDelivery(shipment: any, cardId: string, status: any): string {
    const trackingNumber = shipment.TrackingNumber || '';
    const hasAttachments = shipment.HasAttachments;
    const attachments = shipment.Attachments || [];
    const hasPostalCodeInState = this.state.postalCode && this.state.postalCode.trim();

    return `
      <div id="${cardId}-proof-of-delivery" class="proof-of-delivery lg:flex-1/2">
        <h4 class="text-black text-2xl! font-semibold leading-7 mb-5!">${TranslationHelper.t('pod_title', 'Proof of Delivery')}</h4>

        <!-- Postal Code Input for Attachments -->
        ${hasAttachments ? `
          <div class="form-group">
            <label for="gta_postal_code_${cardId}" class="block text-black text-base! font-normal! mb-3!">
              ${TranslationHelper.t('pod_postal_code_label', 'To view proof of delivery attachments, please enter the delivery postal code.')}
              <span class="text-red-500 font-normal">*</span>
            </label>

            <form class="postal-code-form flex gap-2">
              <input type="hidden" name="tracking_number" value="${trackingNumber}" />
              <div class="relative">
                <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                  <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  id="gta_postal_code_${cardId}" 
                  name="gta_postal_code" 
                  class="gta-input postal-code-input" 
                  placeholder="${TranslationHelper.t('pod_postal_code_placeholder', 'Postal Code')}" 
                  value="${this.state.postalCode || ''}"
                  required />
              </div>
              <button type="submit" class="gta-btn">${TranslationHelper.t('pod_postal_code_button', 'Search')}</button>
            </form>
          </div>

          <!-- Attachments (only show if postal code was verified) -->
          ${attachments.length > 0 && hasPostalCodeInState ? this.renderAttachmentsSection(attachments, trackingNumber, status) : ''}
          
          <!-- Show placeholder or error message -->
          ${attachments.length === 0 ? `
            <div class="attachments-placeholder mt-4 pt-4 border-t border-gray-200">
              <div class="text-center py-4 bg-gray-50 rounded-lg">
                ${hasPostalCodeInState ? `
                  <!-- Invalid postal code error -->
                  <svg class="w-12 h-12 mx-auto text-red-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                  <p class="text-red-500 text-xs! lg:text-sm! font-medium">
                    ${TranslationHelper.t('pod_invalid_postal', 'Invalid postal code. Please check and try again.')}
                  </p>
                ` : `
                  <!-- Default placeholder -->
                  <svg class="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                  <p class="text-gray-600 text-xs! lg:text-sm!">
                    ${TranslationHelper.t('pod_enter_postal', 'Proof of delivery documents are available. Please enter the delivery postal code above to view them.')}
                  </p>
                `}
              </div>
            </div>
          ` : ''}
        ` : ''}
          </div>
    `;
  }

  /**
   * Render attachments section with Swiper gallery
   */
  private renderAttachmentsSection(attachments: string[], trackingNumber: string, status: any): string {
    const deliveredDate = this.getDeliveredDate();
    
    const swiperSlides = attachments.map((attachment, index) => `
      <div class="swiper-slide">
        <div class="attachment-slide-content relative">
        <img 
          src="${attachment}" 
            alt="Proof of Delivery Image ${index + 1}"
            class="rounded-lg cursor-pointer"
            data-modal-target="gta-image-modal"
            data-modal-toggle="gta-image-modal"
          data-image-url="${attachment}"
            loading="lazy"
        />
          <div class="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
            Image ${index + 1}
          </div>
        </div>
      </div>
    `).join('');

    const downloadLinks = attachments.map((attachment, index) => `
      <a href="${attachment}" 
          download="Proof_of_Delivery_Document_${index + 1}_${trackingNumber}.jpg"
          class="download-link flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200">
        <div class="download-icon text-primary-500">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16L7 11L8.4 9.6L11 12.2V4H13V12.2L15.6 9.6L17 11L12 16Z" fill="currentColor"/>
            <path d="M5 20V18H19V20H5Z" fill="currentColor"/>
            </svg>
        </div>
        <div class="download-info">
          <div class="text-sm font-medium text-gray-900">
            Image ${index + 1}
          </div>
          <div class="text-xs text-gray-500">
            Click to download
          </div>
        </div>
      </a>
    `).join('');

    return `
      <div class="attachments mt-4 pt-4 border-t border-gray-200">
        
        <!-- Swiper Gallery -->
        <div class="swiper-container gta-attachments-swiper test" data-tracking="${trackingNumber}">
          <div class="swiper-wrapper">
            ${swiperSlides}
          </div>
          
          <!-- Add Pagination -->
          <div class="swiper-pagination"></div>
          
          <!-- Add Navigation -->
          <div class="swiper-button-next"></div>
          <div class="swiper-button-prev"></div>
        </div>

        <!-- Status and Delivery Information -->
        <div class="status-delivery-info mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div class="flex flex-col gap-2">
            <div>
              <p class="text-sm! font-medium! mb-0!">Current Status:</p>
              <div class="text-sm font-semibold ${this.getStatusColorClass(status.statusLevel)}">
                ${status.stage || 'Unknown'}
              </div>
            </div>
            ${deliveredDate ? `
              <div>
                <span class="text-sm font-medium">${TranslationHelper.t('pod_delivered_date', 'Delivered Date:')}</span>
                <div class="text-sm">
                  <time class="time-convert" data-original="${deliveredDate}">
                    ${this.formatDate(deliveredDate)}
                  </time>
                </div>
              </div>
            ` : ''}
          </div>

          <!-- Download Links Section -->
          <div class="download-links-section mt-6 pt-4 border-t border-gray-200">
            <div class="mb-4">
              <h5 class="text-lg font-semibold text-black mb-2">Download Attachments</h5>
              <!-- Download Links -->
              <div class="download-links-grid">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  ${downloadLinks}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get delivered date from events
   */
  private getDeliveredDate(): string {
    // This should come from the status object or events
    // For now, return empty string - this would be populated by proper status processing
    return '';
  }

  /**
   * Get icon SVG for a detail label
   */
  private getDetailIcon(label: string): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const iconMap: { [key: string]: any } = {
      'Origin': MapPin,
      'Destination': Home,
      'Shipper': User,
      'Consignee': Users,
      'Service': Truck,
      'Status': AlertCircle,
      'Terminal': Building2,
      //'Customer': Briefcase,
      'Weight': Weight, // eslint-disable-line object-shorthand
      //'Volume': Package,
      'Created Date': Calendar,
      'Ship Date': CalendarClock,
      'Delivery Date': CalendarCheck,
      'Total Pieces': Hash,
      'Reference': FileText
    };

    const iconData = iconMap[label];
    if (!iconData) {
      return '';
    }

    // Create an icon element with lucide createElement
    const iconElement = createElement(iconData, {
      class: 'detail-icon',
      width: 16,
      height: 16,
      'stroke-width': 2
    });

    return iconElement.outerHTML;
  }

  /**
   * Render shipment details
   */
  private renderShipmentDetails(shipment: any): string {
    const details = [
      { iconKey: 'Origin', label: TranslationHelper.t('detail_origin', 'Origin'), value: this.formatAddress(shipment, 'Shipper') },
      { iconKey: 'Destination', label: TranslationHelper.t('detail_destination', 'Destination'), value: this.formatAddress(shipment, 'Consignee') },
      { iconKey: 'Shipper', label: TranslationHelper.t('detail_shipper', 'Shipper'), value: shipment.Shipper || '' },
      { iconKey: 'Consignee', label: TranslationHelper.t('detail_consignee', 'Consignee'), value: shipment.Consignee || '' },
      { iconKey: 'Service', label: TranslationHelper.t('detail_service', 'Service'), value: shipment.Service || 'N/A' },
      { iconKey: 'Status', label: TranslationHelper.t('detail_status', 'Status'), value: shipment.Status || '' },
      { iconKey: 'Terminal', label: TranslationHelper.t('detail_terminal', 'Terminal'), value: shipment.Terminal || '' },
      //{ iconKey: 'Customer', label: 'Customer', value: shipment.CustomerName || '' },
      { iconKey: 'Weight', label: TranslationHelper.t('detail_weight', 'Weight'), value: `${shipment.Weight || 0} ${shipment.UOMWeight || ''}`.trim() },
      //{ iconKey: 'Volume', label: 'Volume', value: shipment.Volume ? `${shipment.Volume} ${shipment.UOMLength || ''}`.trim() : '' },
      { iconKey: 'Created Date', label: TranslationHelper.t('detail_created_date', 'Created Date'), value: this.formatDate(shipment.CreateDate || ''), isDate: true },
      { iconKey: 'Ship Date', label: TranslationHelper.t('detail_ship_date', 'Ship Date'), value: this.formatDate(shipment.InductionDate || ''), isDate: true },
      { iconKey: 'Delivery Date', label: TranslationHelper.t('detail_delivery_date', 'Delivery Date'), value: this.formatDate(shipment.DeliveryDate || ''), isDate: true },
      { iconKey: 'Total Pieces', label: TranslationHelper.t('detail_total_pieces', 'Total Pieces'), value: shipment.TotalPieces || '' }
    ];

    // Add references
    const references = [];
    for (let i = 1; i <= 4; i++) {
      const ref = shipment[`Reference${i}`];
      if (ref) {
        references.push(ref);
      }
    }
    if (references.length > 0) {
      details.push({ iconKey: 'Reference', label: TranslationHelper.t('detail_reference', 'Reference'), value: references.join(', ') });
    }

    // Filter out empty details and render
    const detailsHtml = details
      .filter(detail => detail.value)
      .map(detail => `
        <div class="detail-item">
          <span class="detail-label">
            ${this.getDetailIcon(detail.iconKey)}
            ${detail.label}:
          </span>
          <span class="detail-value">
            ${detail.isDate ? `
              <time class="time-convert" data-original="${detail.value}">
                ${this.formatDate(detail.value)}
              </time>
            ` : detail.value}
          </span>
        </div>
      `).join('');

    return `
      <div class="shipment-details grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
        ${detailsHtml}
      </div>
    `;
  }

  /**
   * Format address from shipment data
   */
  private formatAddress(shipment: any, prefix: string): string {
    const parts = [
      shipment[`${prefix}Address1`] || '',
      shipment[`${prefix}Address2`] || '',
      shipment[`${prefix}City`] || '',
      shipment[`${prefix}Province`] || '',
      shipment[`${prefix}PostalCode`] || ''
    ].filter(part => part.trim());
    
    return parts.join(', ');
  }

  /**
   * Check if package details should be shown (only for local/qa environments)
   */
  private shouldShowPackageDetails(): boolean {
    // const urlParams = new URLSearchParams(window.location.search);
    // const queryTo = urlParams.get('queryto');
    // return queryTo === 'local' || queryTo === 'qa';

    return true;
  }


  /**
   * Render no results message
   */
  private renderNoResults(): void {
    if (!this.resultsContainer) {return;}

    // Create a not-found message that matches the PHP template style
    const trackingNumbers = this.apiService.parseTrackingNumbers(this.state.trackingNumbers);
    const notFoundHtml = trackingNumbers.map(trackingNumber => 
      this.renderShipmentNotFound(trackingNumber)
    ).join('');

    this.resultsContainer.innerHTML = `
      <div class="gta-shipments-container">
        ${notFoundHtml}
      </div>
    `;
    this.resultsContainer.style.display = 'block';
  }

  /**
   * Render shipment not found card
   */
  private renderShipmentNotFound(trackingNumber: string): string {
    return `
      <div class="gta-shipment-card animate-fade-in">
        <div class="gta-card-body text-center py-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            ${TranslationHelper.t('not_found_title', 'Shipment Not Found')}
          </h3>
          
          <p class="text-gray-600 mb-4">
            ${TranslationHelper.t('not_found_description', 'No tracking information found for:')}
          </p>
          
          <div class="bg-warning-50 border border-warning-200 rounded px-4 py-2 mb-4">
            <code class="text-warning-800 font-mono font-semibold">
              ${trackingNumber}
            </code>
          </div>
          
          <div class="text-sm text-gray-500 space-y-2">
            <p>${TranslationHelper.t('not_found_reasons_title', 'This could mean:')}</p>
            <ul class="list-disc list-inside space-y-1 text-left max-w-md">
              <li>${TranslationHelper.t('not_found_reason_1', 'The tracking number was entered incorrectly')}</li>
              <li>${TranslationHelper.t('not_found_reason_2', 'The shipment has not been processed yet')}</li>
              <li>${TranslationHelper.t('not_found_reason_3', 'The tracking number is for a different carrier')}</li>
              <li>${TranslationHelper.t('not_found_reason_4', 'The shipment is too old and no longer in the system')}</li>
            </ul>
          </div>
          
          <div class="mt-6 flex flex-row  gap-3 justify-center">
            <button type="button" 
                    class="gta-btn btn-primary btn-sm"
                    onclick="document.querySelector('[data-tracking-input]')?.focus()">
              ${TranslationHelper.t('not_found_try_another', 'Try Another Number')}
            </button>
            
            <a href="mailto:customerservice@gtagsm.com" 
               class="gta-btn btn-secondary btn-sm no-underline!">
              ${TranslationHelper.t('not_found_contact_support', 'Contact Support')}
            </a>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Show loading state
   */
  private showLoading(): void {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'flex';
    }
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'none';
    }
  }

  /**
   * Hide loading state
   */
  private hideLoading(): void {
    if (this.loadingContainer) {
      this.loadingContainer.style.display = 'none';
    }
  }

  /**
   * Show error message - relies on server-side error-message.php template for consistency
   * Only handles minimal client-side validation errors
   */
  private showError(message: string): void {
    this.setState({ error: message });

    // For API errors, let server handle them. For client validation, show simple message
    console.error('TrackingApp Error:', message);

    // Remove existing error
    const existingError = this.container.querySelector('.gta-error-message');
    if (existingError) {
      existingError.remove();
    }

    // Simple client-side error display
    const errorContainer = document.createElement('div');
    errorContainer.className =
      'gta-error-message bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4';
    errorContainer.textContent = message;

    // Insert after form or at beginning
    const insertAfter = this.searchForm || this.container.firstElementChild;
    if (insertAfter && insertAfter.parentNode === this.container) {
      // Insert after the insertAfter element
      if (insertAfter.nextSibling) {
        this.container.insertBefore(errorContainer, insertAfter.nextSibling);
      } else {
        this.container.appendChild(errorContainer);
      }
    } else {
      // Fallback: prepend to container
      this.container.insertBefore(errorContainer, this.container.firstChild);
    }
  }

  /**
   * Clear error message
   */
  private clearError(): void {
    this.setState({ error: null });

    const errorContainer = this.container.querySelector('.gta-error-message') as HTMLElement;
    if (errorContainer) {
      errorContainer.style.display = 'none';
    }
  }

  /**
   * Get current state
   */
  public getState(): TrackingFormState {
    return { ...this.state };
  }

  /**
   * Get configuration
   */
  public getConfig(): TrackingConfig {
    return this.config;
  }
}
